import { useEffect, useId, useState } from "react";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import dayjs from "dayjs";
import { MdDelete, MdExpandMore, MdPerson } from "react-icons/md";
import AtriumApi, { getApiErrorMessage } from "api";
import { track } from "lib/mixpanel";
import { EVENTS } from "lib/events";
import notify from "lib/notify";
import Avatar from "components/avatar";
import Button from "components/button";
import Card, { CardHeader } from "components/card";
import { useConfirm } from "components/dialog/ConfirmProvider";
import DatePicker from "components/fields/DatePicker";
import InputField from "components/fields/InputField";
import Select from "components/fields/Select";
import MapboxAddressInput from "components/mapbox";
import TagSelector from "components/tags/TagSelector";

const GENDER_OPTIONS = [
  { value: "male", label: "Male" },
  { value: "female", label: "Female" },
];

const RELATIONSHIP_OPTIONS = [
  { value: "primary", label: "Primary" },
  { value: "married", label: "Married" },
  { value: "child", label: "Child" },
  { value: "unknown", label: "Unknown" },
];

const ROLE_OPTIONS = [
  { value: "guest", label: "Guest" },
  { value: "member", label: "Member" },
  { value: "volunteer", label: "Volunteer" },
  { value: "staff", label: "Staff" },
  { value: "deacon", label: "Deacon" },
  { value: "elder", label: "Elder" },
];

const STATUS_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
];

// MM/DD/YYYY is the DatePicker's text input format.
const formatToMMDDYYYY = (date) => {
  if (!date) return "";
  const parsed = dayjs(date);
  if (!parsed.isValid()) return "";
  return parsed.format("MM/DD/YYYY");
};

/**
 * A form section: a Card on the edit page; a plain divided section when the
 * form is embedded in a drawer (no cards inside the drawer panel).
 */
const FormSection = ({ embedded, flush = false, className = "", children }) =>
  embedded ? (
    <section className="flex flex-col gap-5 border-t border-line pt-5 first:border-t-0 first:pt-0">
      {children}
    </section>
  ) : (
    <Card padding={flush ? "none" : "md"} className={className}>
      {children}
    </Card>
  );

/**
 * Create/edit form for a person (Formik + Yup).
 *
 * @param {object} props
 * @param {object} [props.user] Existing person; omit to create.
 * @param {() => void} props.onClose Called on Cancel, after a save and after a delete.
 * @param {() => Promise|void} [props.onSuccess] Awaited after a successful save, before onClose.
 * @param {string} [props.formId="user-form"] Lets an external submit button target the form.
 * @param {boolean} [props.hideActions=false] Hide the built-in action row (the drawer renders
 *   its own footer).
 * @param {(submitting: boolean) => void} [props.onSubmittingChange]
 */
const UserForm = (props) => {
  const {
    onClose,
    onSuccess,
    formId = "user-form",
    hideActions = false,
    onSubmittingChange,
  } = props;

  const confirm = useConfirm();
  const uploadId = useId();
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [user, setUser] = useState(
    {
      role: props?.user?.role || "",
      status: props?.user?.status || "",
      campus: props?.user?.campus || "",
      firstName: props?.user?.firstName || "",
      lastName: props?.user?.lastName || "",
      email: props?.user?.email || "",
      phone: props?.user?.phone || "",
      dob: props?.user?.dob || "",
      familyRelationship: props?.user?.familyRelationship || "",
      gender: props?.user?.gender || "",
      avatar: props?.user?.avatar?.url || "",
      address: props?.user?.address || "",
      city: props?.user?.city || "",
      street: props?.user?.street || "",
      state: props?.user?.state || "",
      zip: props?.user?.zip || "",
      country: props?.user?.country || "",
      id: props?.user?.id || "",
    } || {}
  );

  const [image, setImage] = useState(props.user?.avatar?.url || "");
  const [imageFile, setImageFile] = useState(null);

  const [location, setLocation] = useState(
    {
      type: "Point",
      coordinates: [props?.user?.lng, props?.user?.lat],
    } || {}
  );

  const [campuses, setCampuses] = useState([]);
  const [households, setHouseholds] = useState([]);
  const [selectedHouseholdId, setSelectedHouseholdId] = useState("");
  const [showLocation, setShowLocation] = useState(false);
  const [selectedTags, setSelectedTags] = useState(props?.user?.tags || []);

  useEffect(() => {
    onSubmittingChange?.(submitting);
  }, [submitting, onSubmittingChange]);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const imageUrl = URL.createObjectURL(file);
      setImage(imageUrl);
      setImageFile(file);
    }
  };

  useEffect(() => {
    const fetchAllCampuses = async () => {
      try {
        const response = await AtriumApi.get("/v2/campuses/allCampuses");
        if (response) {
          setCampuses(response?.data?.data?.campuses?.data);
        }
      } catch (error) {
        // Silently handle error; the select shows "No campuses found"
      }
    };

    const fetchHouseholds = async () => {
      try {
        const response = await AtriumApi.get("/v1/get-households");
        if (response?.data?.data) {
          setHouseholds(response.data.data);
        }
      } catch (error) {
        // Silently handle error - households are optional
      }
    };

    fetchAllCampuses();
    fetchHouseholds();
  }, []);

  useEffect(() => {
    if (!user.id && props.user?.id) {
      setUser({
        role: props?.user?.role,
        status: props?.user?.status,
        campus: props?.user?.campus,
        firstName: props?.user?.firstName,
        lastName: props?.user?.lastName,
        email: props?.user?.email,
        phone: props?.user?.phone,
        dob: props?.user?.dob,
        familyRelationship: props?.user?.familyRelationship,
        gender: props?.user?.gender,
        avatar: props?.user?.avatar?.url,
        address: props?.user?.address,
        city: props?.user?.city,
        street: props?.user?.street,
        state: props?.user?.state,
        zip: props?.user?.zip,
        country: props?.user?.country,
        id: props?.user?.id,
      });
      setImage(props?.user?.avatar?.url);
      setLocation({
        type: "Point",
        coordinates: [props?.user?.lng, props?.user?.lat],
      });
      setSelectedTags(props?.user?.tags || []);
    }
  }, [user, props.user]);

  const getValidationSchema = (isEdit) =>
    Yup.object().shape(
      {
        // Always required
        role: Yup.string().required("Role is required"),
        status: Yup.string().required("Status is required"),
        campus: Yup.string().required("Campus is required"),
        firstName: Yup.string().required("First name is required"),
        lastName: Yup.string().required("Last name is required"),
        familyRelationship: Yup.string().required(
          "Family relationship is required"
        ),
        gender: Yup.string().required("Gender is required"),

        // Email OR Phone required (conditional)
        email: Yup.string()
          .email("Invalid email format")
          .when("phone", {
            is: (phone) => !phone || phone.trim() === "",
            then: (schema) =>
              schema.required("Email is required when phone is not provided"),
            otherwise: (schema) => schema.notRequired(),
          }),
        phone: Yup.string()
          .matches(
            /^[0-9\s\-()+]*$/,
            "Phone number should only contain numbers."
          )
          .nullable()
          .when("email", {
            is: (email) => !email || email.trim() === "",
            then: (schema) =>
              schema.required("Phone is required when email is not provided"),
            otherwise: (schema) => schema.notRequired(),
          }),

        // Optional fields
        address: Yup.string().nullable().notRequired(),
        dob: Yup.string().nullable().notRequired(),
      },
      [["email", "phone"]] // Cyclic dependency declaration
    );

  const onSubmit = (values) => {
    setSubmitting(true);

    let requestData;
    let config = {};

    const lat = location?.coordinates?.[1] || null;
    const lng = location?.coordinates?.[0] || null;

    // Use FormData only when uploading a new file
    if (imageFile) {
      const formData = new FormData();
      Object.keys(values).forEach((key) => {
        if (key !== "avatar") {
          formData.append(key, values[key]);
        }
      });
      formData.append("location", JSON.stringify(location));
      if (lat) formData.append("lat", lat);
      if (lng) formData.append("lng", lng);
      formData.append("avatar", imageFile);
      if (selectedHouseholdId) {
        formData.append("householdId", selectedHouseholdId);
      }
      if (selectedTags && selectedTags.length > 0) {
        formData.append("tags", JSON.stringify(selectedTags.map((tag) => tag.id)));
      }

      requestData = formData;
      // The axios interceptor in api.jsx handles Content-Type for FormData
      config = {};
    } else {
      requestData = {
        ...values,
        location: location,
        lat: lat,
        lng: lng,
      };
      if (selectedHouseholdId) {
        requestData.householdId = selectedHouseholdId;
      }
      if (selectedTags && selectedTags.length > 0) {
        requestData.tags = selectedTags.map((tag) => tag.id);
      }
      delete requestData.avatar; // The backend keeps the existing avatar
    }

    const request = user?.id
      ? AtriumApi.put(`/v1/users/${user?.id}`, requestData, config)
      : AtriumApi.post("/v1/users", requestData, config);

    request
      .then(async (response) => {
        if (response.status === 422) {
          notify.error(response.data.message);
        } else if (response.status === 200) {
          if (response.data?.user) {
            const updatedUser = response.data.user;
            setUser(updatedUser);
            if (updatedUser.avatar?.url) {
              setImage(updatedUser.avatar.url);
              setImageFile(null);
            }
          }

          if (user.id) {
            track(EVENTS.MEMBER_UPDATED, { member_id: user.id });
          } else {
            track(EVENTS.MEMBER_CREATED);
          }

          notify.success(user.id ? "Profile saved" : "Person created");

          if (onSuccess) {
            await onSuccess();
          }

          onClose();
        } else {
          throw new Error("unexpected response");
        }
      })
      .catch((error) => {
        notify.error(
          getApiErrorMessage(error, user.id ? "Could not save profile" : "Could not create person")
        );
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  const handleDelete = async () => {
    const fullName = [user?.firstName, user?.lastName].filter(Boolean).join(" ");
    const confirmed = await confirm({
      title: `Delete "${fullName || "this person"}"?`,
      message: "This person and their profile will be permanently deleted.",
      confirmLabel: "Delete",
      tone: "danger",
    });
    if (!confirmed) return;
    try {
      setDeleting(true);
      await AtriumApi.delete(`/v1/deleteUser/${user?.id}`);
      track(EVENTS.MEMBER_DELETED);
      notify.success("Person deleted");
      onClose();
    } catch (err) {
      notify.error(getApiErrorMessage(err, "Could not delete person"));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Formik
      enableReinitialize
      initialValues={user || {}}
      validationSchema={getValidationSchema(!!user?.id)}
      onSubmit={onSubmit}
    >
      {({
        values,
        errors,
        touched,
        handleChange,
        handleBlur,
        handleSubmit,
        setFieldValue,
      }) => {
        const fieldProps = (name) => ({
          name,
          value: values[name] || "",
          onChange: handleChange,
          onBlur: handleBlur,
          error: errors[name],
          touched: touched[name],
        });
        return (
          <Form
            id={formId}
            onSubmit={(e) => {
              e.preventDefault();
              handleSubmit();
            }}
            noValidate
            aria-busy={submitting || undefined}
            className="flex flex-col gap-5"
          >
            <FormSection embedded={hideActions} className="gap-5">
              <CardHeader title="Basic Information" />

              <div className="flex flex-col items-center gap-3">
                {image || values.firstName || values.lastName ? (
                  <Avatar
                    src={image || undefined}
                    name={[values.firstName, values.lastName].filter(Boolean).join(" ")}
                    size="xl"
                    noBorder
                  />
                ) : (
                  <span className="flex h-[72px] w-[72px] items-center justify-center rounded-full bg-surface-subtle text-fg-muted">
                    <MdPerson aria-hidden="true" className="h-8 w-8" />
                  </span>
                )}
                <label
                  htmlFor={uploadId}
                  className="inline-flex h-8 cursor-pointer items-center rounded-control border border-line bg-surface px-3 text-xs font-medium text-fg transition-colors hover:bg-surface-subtle focus-within:ring-2 focus-within:ring-focus"
                >
                  Upload Picture
                  <input
                    id={uploadId}
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    className="sr-only"
                  />
                </label>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <InputField label="First Name" required {...fieldProps("firstName")} />
                <InputField label="Last Name" required {...fieldProps("lastName")} />
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <DatePicker
                  name="dob"
                  isFutureDateDisabled
                  label="Date of Birth"
                  height={30}
                  value={
                    values.dob
                      ? /^\d{4}-\d{2}-\d{2}$/.test(values.dob)
                        ? formatToMMDDYYYY(values.dob)
                        : values.dob
                      : ""
                  }
                  onChange={(v) => {
                    // Calendar selection passes a Date; typing passes MM/DD/YYYY text.
                    if (typeof v === "string") {
                      if (v.length === 10 && v.includes("/")) {
                        const parsed = dayjs(v, "MM/DD/YYYY");
                        setFieldValue("dob", parsed.isValid() ? parsed.format("YYYY-MM-DD") : v);
                      } else {
                        setFieldValue("dob", v);
                      }
                    } else {
                      setFieldValue("dob", dayjs(v).format("YYYY-MM-DD"));
                    }
                  }}
                  onBlur={handleBlur}
                />
                <Select
                  label="Gender"
                  required
                  placeholder="Select gender"
                  options={GENDER_OPTIONS}
                  {...fieldProps("gender")}
                />
              </div>

              <Select
                label="Family Relationship"
                required
                hint="Primary: main household contact. Married: spouse. Child: dependent."
                placeholder="Select relationship"
                options={RELATIONSHIP_OPTIONS}
                {...fieldProps("familyRelationship")}
              />
            </FormSection>

            <FormSection embedded={hideActions} className="gap-5">
              <CardHeader
                title="Contact Information"
                description="Provide at least one contact method: email or phone number."
              />
              <div className="grid gap-4 md:grid-cols-2">
                <InputField
                  label="Email Address"
                  type="email"
                  required={!values.phone || values.phone.trim() === ""}
                  {...fieldProps("email")}
                />
                <InputField
                  label="Phone Number"
                  type="tel"
                  required={!values.email || values.email.trim() === ""}
                  {...fieldProps("phone")}
                />
              </div>
            </FormSection>

            <FormSection embedded={hideActions} className="gap-5">
              <CardHeader title="Organization Settings" />
              <div className="grid gap-4 md:grid-cols-2">
                <Select
                  label="Role"
                  required
                  placeholder="Select a role"
                  options={ROLE_OPTIONS}
                  {...fieldProps("role")}
                />
                <Select
                  label="Status"
                  required
                  placeholder="Select status"
                  options={STATUS_OPTIONS}
                  {...fieldProps("status")}
                />
              </div>
              <Select
                label="Campus"
                required
                placeholder="Select a campus"
                {...fieldProps("campus")}
              >
                {campuses?.length > 0 ? (
                  campuses.map((campus) => (
                    <option key={campus?.id} value={campus?.id}>
                      {campus?.name}
                    </option>
                  ))
                ) : (
                  <option value="" disabled>
                    No campuses found
                  </option>
                )}
              </Select>
              <Select
                label="Add to Household"
                hint="Optional"
                value={selectedHouseholdId}
                onChange={(e) => setSelectedHouseholdId(e.target.value)}
              >
                <option value="">Don't add to household</option>
                {households.map((h) => (
                  <option key={h.id} value={h.id}>
                    {h.name}
                  </option>
                ))}
              </Select>
            </FormSection>

            <FormSection embedded={hideActions} flush className="overflow-hidden">
              <button
                type="button"
                aria-expanded={showLocation}
                aria-controls={`${formId}-address`}
                onClick={() => setShowLocation(!showLocation)}
                className={`flex w-full items-center justify-between gap-2 text-left transition-colors hover:bg-surface-subtle focus-ring ${
                  hideActions ? "rounded-control py-2" : "p-6"
                }`}
              >
                <span className="flex items-baseline gap-2">
                  <span className="text-heading">Address</span>
                  <span className="text-xs text-fg-muted">Optional</span>
                </span>
                <MdExpandMore
                  aria-hidden="true"
                  className={`h-5 w-5 text-fg-muted transition-transform duration-200 ${
                    showLocation ? "rotate-180" : ""
                  }`}
                />
              </button>

              {showLocation && (
                <div
                  id={`${formId}-address`}
                  className={`flex flex-col gap-4 ${hideActions ? "pt-3" : "px-6 pb-6"}`}
                >
                  <MapboxAddressInput
                    label="Address"
                    id="address"
                    name="address"
                    value={values?.address || props.user?.address || ""}
                    onResult={(data) => {
                      setLocation({
                        type: "Point",
                        coordinates: [data?.lng, data?.lat],
                      });

                      if (data) {
                        setFieldValue("address", data?.address || "");
                        setFieldValue("state", data?.state || "");
                        setFieldValue("city", data?.city || "");
                        setFieldValue("street", data?.street || data?.address || "");
                        setFieldValue("zip", data?.zipCode || "");
                      }
                    }}
                    onClearInput={() => {
                      setLocation({});
                      setFieldValue("address", "");
                      setFieldValue("state", "");
                      setFieldValue("city", "");
                      setFieldValue("street", "");
                      setFieldValue("zip", "");
                    }}
                  />
                  <div className="grid gap-4 md:grid-cols-2">
                    <InputField label="State" {...fieldProps("state")} />
                    <InputField label="City" {...fieldProps("city")} />
                  </div>
                  <div className="grid gap-4 md:grid-cols-2">
                    <InputField label="Street Address" {...fieldProps("street")} />
                    <InputField label="Zip Code" {...fieldProps("zip")} />
                  </div>
                </div>
              )}
            </FormSection>

            <FormSection embedded={hideActions} className="gap-4">
              <CardHeader title="Tags" description="Optional" />
              <TagSelector
                userId={user.id}
                selectedTags={selectedTags}
                onTagsChange={setSelectedTags}
              />
            </FormSection>

            {!hideActions && (
              <div className="flex flex-wrap items-center gap-2">
                {user.id && (
                  <Button
                    variant="danger"
                    leftIcon={<MdDelete className="h-4 w-4" />}
                    loading={deleting}
                    onClick={handleDelete}
                  >
                    Delete
                  </Button>
                )}
                <div className="ml-auto flex items-center gap-2">
                  <Button variant="secondary" onClick={onClose}>
                    Cancel
                  </Button>
                  <Button type="submit" loading={submitting}>
                    {submitting ? "Saving…" : user.id ? "Save Changes" : "Create Person"}
                  </Button>
                </div>
              </div>
            )}
          </Form>
        );
      }}
    </Formik>
  );
};

export default UserForm;
